import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { activeClientState, dataVersionState } from '../../state.js';
import { Card, EmptyState } from '../Card.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner } from '../FormBits.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';
import { fmtNum } from '../../utils.js';
import { snapshots, type Snapshot } from './pdfBatchApi.js';

/**
 * Every figure the client's report is built from, and where it came from.
 *
 * Undoing a whole upload only reaches back as far as batch tracking goes; a
 * snapshot that landed before it, or one from a workbook, can only be fixed
 * here. The alternative is hand-edited SQL against production, which is how
 * numbers get quietly broken.
 */
export function SnapshotsCard() {
  const bump = useSetRecoilState(dataVersionState);
  const clientId = useRecoilValue(activeClientState) ?? '';
  const [rows, setRows] = useState<Snapshot[] | null>(null);
  const [doomed, setDoomed] = useState<Snapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRows(null); // never the previous client's rows under the new client's name
    setError(null);
    if (!clientId) return;
    let live = true;
    snapshots.list(clientId)
      .then((r) => live && setRows(r))
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);

  const remove = async () => {
    if (!doomed) return;
    setBusy(true);
    setError(null);
    try {
      await snapshots.remove(doomed.holding_id);
      setRows(await snapshots.list(clientId));
      bump((v) => v + 1);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setDoomed(null);
    }
  };

  // A return is measured against the month before it, so deleting one month
  // does not damage every later month equally: only the NEXT month that has a
  // return loses what it was measured against. Months after that still have
  // their own predecessor. What DOES carry forward from there is the cumulative
  // figure, because it compounds — so that is named separately rather than
  // lumped in.
  const nextAffected = doomed?.report_date
    ? (rows ?? [])
        .filter((r) => r.fund === doomed.fund && r.report_date && r.report_date > doomed.report_date! && r.has_return)
        .map((r) => r.report_date!)
        .sort()[0] ?? null
    : null;

  return (
    <Card>
      <ConfirmDialog
        open={!!doomed}
        title="למחוק את תמונת המצב?"
        confirmLabel="מחק"
        onConfirm={() => void remove()}
        onCancel={() => setDoomed(null)}
      >
        <p>
          <span className="font-semibold">{doomed?.fund}</span> לתאריך{' '}
          <span className="font-semibold" dir="ltr">{doomed?.report_date ?? '—'}</span> יימחק,
          יחד עם ניירות הערך והתשואה שנגזרו ממנו.
        </p>
        <p className="mt-2 text-muted-foreground">
          אם זו תמונת המצב האחרונה של הקרן, הפורטל יחזור להציג את הקודמת. הפעולה אינה הפיכה.
        </p>
        {nextAffected && (
          <p className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-warning">
            התשואה של <span dir="ltr">{nextAffected}</span> נמדדת מול החודש הזה. אם תמחקו אותו, היא
            תימדד מול חודש רחוק יותר — או לא תחושב כלל — והתשואה המצטברת מאותו חודש והלאה תשתנה
            בהתאם.
          </p>
        )}
      </ConfirmDialog>

      <div className="grid gap-4">
        <p className="text-xs text-muted-foreground">
          כל מספר שהדוח של הלקוח בנוי עליו, ומאיפה הוא הגיע.
        </p>

        {!clientId && <EmptyState label="בחרו לקוח למעלה" />}
        {clientId && rows?.length === 0 && <EmptyState label="אין תמונות מצב ללקוח הזה" />}

        {rows && rows.length > 0 && (
          <Table minWidth="52rem">
            <THeadRow>
              <TH>קרן</TH>
              <TH>תאריך דוח</TH>
              <TH align="end">סה"כ</TH>
              <TH align="end">ניירות</TH>
              <TH>מקור</TH>
              <TH>יתרה בפורטל</TH>
              <TH>תשואה</TH>
              <TH align="end" />
            </THeadRow>
            <tbody>
              {rows.map((s) => (
                <TR key={s.holding_id}>
                  <TD className="font-medium">{s.fund}</TD>
                  <TD><Ltr>{s.report_date ?? 'ללא תאריך'}</Ltr></TD>
                  <TD align="end" className="tabular-nums">{fmtNum(s.total_value)}</TD>
                  <TD align="end" className="tabular-nums text-muted-foreground">{s.positions || '—'}</TD>
                  <TD className="max-w-[14rem] truncate text-xs text-muted-foreground" title={s.source}>
                    {s.source}
                  </TD>
                  {/* Two different questions. is_latest only says this is the fund's
                      newest balance; the report itself is built from the return,
                      and a snapshot can have the first without the second. One
                      column headed "מוצג בפורטל" answered both at once, with כן. */}
                  <TD>
                    {s.is_latest
                      ? <span className="text-success">מוצגת</span>
                      : <span className="text-muted-foreground">היסטורית</span>}
                  </TD>
                  <TD>
                    {/* Three states, not two. A fund's FIRST month has a return
                        row and still no return — there is no earlier month to
                        measure against — and calling that "מחושבת" would be the
                        same overclaiming this column replaced. */}
                    {!s.has_return
                      ? <span className="text-warning" title="בלי הפקדות ומשיכות אי אפשר לחשב תשואה לחודש הזה">
                          חסרה
                        </span>
                      : s.has_prev_return
                        ? <span className="text-success">מחושבת</span>
                        : <span className="text-muted-foreground" title="זהו החודש הראשון של הקרן — אין חודש קודם למדוד מולו">
                            חודש ראשון
                          </span>}
                  </TD>
                  <TD align="end">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setDoomed(s)}
                      className="rounded-md border border-destructive/40 px-2 py-0.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    >
                      מחיקה
                    </button>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}

        {rows && rows.some((r) => !r.has_return) && (
          <p className="text-xs text-muted-foreground">
            <span className="text-warning">תשואה חסרה</span> — היתרה של החודש מופיעה בפורטל, אבל
            הדוח עצמו (תשואות, גרפים, החודש שהדוח מתייחס אליו) לא זז בלעדיה. כדי להשלים: העלו את
            הקובץ של אותו חודש שוב דרך "עדכון חודשי" ואשרו בו הפקדות ומשיכות.
          </p>
        )}
      </div>

      <ErrorBanner error={error} title="הפעולה נכשלה" />
    </Card>
  );
}
